import './ReactStoreCarousel.scss'
import { useEffect, useRef } from 'react'

import { useAppDataContext } from '@cox/core-ui8/dist/AppDataContext'
import { AlignmentProps, ButtonTypes, LinkTypes } from '@cox/core-ui8/dist/Button'

import {
  CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON,
  CAROUSEL_INACTIVE_ARROW_BUTTON,
  COX_MOBILE_LOGO
} from '../../../assets/images'
import { INVERSION_PAGE_TYPES } from '../../../constants'
import { useInversionData } from '../../../hooks/useInversionData'
import { scrollToCarouselElementWithOffset } from '../../../utils/helper-util'
import { formatAddressToLink, getAddressLine2, getStoreStatus } from '../../../utils/inversion-util'
import { ScreenSizes } from '../../../utils/types'
import ReactButton from '../../ReactButton'
import ReactImage from '../../ReactImage'
import ReactText from '../../ReactText'
import ActionButton from '../ActionButton'
import CustomDot from '../CustomDot'
import ReactMultiCarouselV2 from '../ReactMultiCarouselV2'
import { ActionButtonTypes, responsiveStoreCarouselProps } from '../types'

interface StoreCarouselRef {
  goToSlide: (slide: number) => void
}
const ReactStoreCarousel = (props: any): JSX.Element => {
  const {
    id = '',
    title,
    description,
    carouselActiveButton,
    carouselDisabledButton,
    carouselActiveButtonAltText = 'Next',
    carouselDisabledButtonAltText = 'Previous',
    activePip,
    inActivePip,
    activePipAltText,
    inActivePipAltText,
    activeAuthoredIndex,
    selectedSlideId,
    resetSlideId,
    isInEditor = false,
    bottomImage = COX_MOBILE_LOGO
  } = props

  const slideIdMap = new Map()
  const storeCarouselRef = useRef<StoreCarouselRef | any>(null)
  let inversionItems = useInversionData()
  const { appData } = useAppDataContext()
  const { pageType = '' } = appData?.page ?? {}
  let nearbyStoreTitle = ''
  const isNearbyStores = inversionItems?.[0]?.stores_nearby?.length > 0
  if (pageType === INVERSION_PAGE_TYPES.stores_address && inversionItems?.length > 0) {
    inversionItems = inversionItems[0]?.stores_nearby
  }
  if (isNearbyStores) {
    nearbyStoreTitle = title
  } else {
    nearbyStoreTitle = ''
  }
  useEffect(() => {
    if (selectedSlideId) {
      const index = slideIdMap.get(selectedSlideId)
      if (index > -1) {
        const cardsLength = inversionItems?.length
        let indexToScrollTo = index
        if (window.innerWidth >= ScreenSizes.LG && index > cardsLength - 3) {
          indexToScrollTo = index - 2
          if (indexToScrollTo < 0) {
            indexToScrollTo = 0
          }
        }
        scrollToCarouselElementWithOffset({}, '#', selectedSlideId)
        storeCarouselRef?.current?.goToSlide(indexToScrollTo)
      }
    }
    return () => {
      resetSlideId?.()
    }
  }, [selectedSlideId])

  const getCarouselItems = () => {
    const carouselItems = inversionItems?.map((sc: any, index: any) => {
      const slideIdToSet = index
      slideIdMap.set(slideIdToSet, index)
      return (
        <StoreCarouselSlide
          key={`${index + 1}_store`}
          {...inversionItems}
          pageType={pageType}
          bottomImage={bottomImage}
          elements={sc}
          index={index}
          id={index}
        />
      )
    })
    //filters out any empty values
    return carouselItems?.filter((elm: any) => elm)
  }

  const carouselItems = getCarouselItems()

  return (
    <div data-testid={`react-store-carousel`} className={`react-store-carousel`}>
      <div className='store-carousel-container'>
        <div className='react-carousel responsive-size'>
          <ReactMultiCarouselV2
            ref={storeCarouselRef}
            //custom props
            id={id}
            title={nearbyStoreTitle}
            description={description}
            //multicarousel props
            ssr={true}
            partialVisible={true}
            responsive={responsiveStoreCarouselProps}
            arrows={false}
            renderButtonGroupOutside={carouselItems?.length > 2}
            customButtonGroup={
              <ButtonGroup
                nextButton={carouselActiveButton}
                previousButton={carouselDisabledButton}
                nextButtonAltText={carouselActiveButtonAltText}
                previousButtonAltText={carouselDisabledButtonAltText}
              />
            }
            showDots={carouselItems?.length > 1}
            renderDotsOutside={true}
            swipeable={true}
            customDot={
              <CustomDot
                activePip={activePip}
                inActivePip={inActivePip}
                activePipAltText={activePipAltText}
                inActivePipAltText={inActivePipAltText}
              />
            }
            activeAuthoredIndex={activeAuthoredIndex}
            className={carouselItems?.length < 3 ? 'align-carousel-items-center' : 'align-carousel-items-flex-start'}
            isInEditor={isInEditor}
          >
            {carouselItems?.length > 0 &&
              carouselItems?.map((carouselItem: any) => {
                return (
                  <div className='carousel-item-wrapper' key={JSON.stringify(carouselItem.key)}>
                    {carouselItem}
                  </div>
                )
              })}
          </ReactMultiCarouselV2>
        </div>
      </div>
    </div>
  )
}

// get value if it is dynamic, or else return value authored value

const StoreCarouselSlide = (carouselItems: any) => {
  const { buttonLinkTypePrimary, index, id = '', bottomImage } = carouselItems
  const {
    name,
    address_line1,
    address_line2,
    address_city,
    address_state,
    address_zip,
    store_phonenumber,
    store_tags,
    directions_link,
    slug
  } = carouselItems.elements

  const primaryButtonType = buttonLinkTypePrimary || ButtonTypes.PRIMARY
  const headerText = name
  const addressText = `<div className='store-address'><span>
    ${address_line1}<br/>
    ${getAddressLine2(address_line2) && `${getAddressLine2(address_line2)}<br/>`}
    ${address_city}, ${address_state} ${address_zip}
    </span>
    <span className='store-phone'>
    ${store_phonenumber}
    </span></div>`
  const currentTime = new Date()
  const storeStatus = getStoreStatus(carouselItems?.elements, currentTime)
  const storeLinkTxt = `Visit Store Page`
  const currentPath = window.location.pathname.replace('.html', '/') ?? ''
  let storeLink = `${currentPath}${slug}.html`

  if (carouselItems?.pageType === INVERSION_PAGE_TYPES.stores_address) {
    storeLink = `/residential/local/stores/${address_state.toLowerCase()}/${address_city
      .split(' ')
      .join('-')
      .toLowerCase()
      .replace(/\./g, '')}/${formatAddressToLink(address_line1)}`
  }

  return (
    <div className='carousel-item-container' id={id}>
      {/* Top */}
      <div className='top-section'>
        <div className='top-section-card-header'>
          {/* Header */}
          {headerText && <ReactText text={`${headerText} <br/> ${storeStatus}`} isParsed={false} className='header' />}
        </div>
        <div className='top-section-card-body'>
          {/* Description */}
          {addressText && <ReactText text={addressText} isParsed={false} className='body-description' />}
        </div>
      </div>

      {/* Buttons */}
      <div className='navigation-section'>
        {directions_link &&
          directions_link?.length > 0 &&
          directions_link?.toLowerCase() !== 'null' &&
          directions_link != null && (
            <div className='button-section'>
              <ReactButton
                text={'Get Directions'}
                buttonTypes={primaryButtonType as ButtonTypes}
                linkType={LinkTypes.PAGE}
                alignment={AlignmentProps.LEFT}
                linkUrl={directions_link}
                index={index}
                openInNewTab={true}
              />
            </div>
          )}
        {/* Links */}
        {storeLink && storeLink?.length > 0 && (
          <div className='link-section'>
            <div>
              <ReactButton
                text={storeLinkTxt}
                className='store_details-link'
                aria-label={'Store Details Link'}
                buttonTypes={ButtonTypes.TERTIARY}
                linkType={LinkTypes.PAGE}
                alignment={AlignmentProps.LEFT}
                linkUrl={storeLink}
                openInNewTab={true}
              />
            </div>
          </div>
        )}
      </div>
      {/* Bottom Image*/}
      {bottomImage && store_tags?.toLowerCase() !== 'null' && store_tags != null && (
        <div className='bottom-image-section'>
          {bottomImage.length > 0 && (
            <div>
              <ReactImage src={bottomImage} alt={'Cox Mobile Logo'} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ButtonGroup = (props: any) => {
  const {
    carouselState: { currentSlide, totalItems },
    next,
    previous,
    nextButton = CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON,
    previousButton = CAROUSEL_INACTIVE_ARROW_BUTTON,
    nextButtonAltText,
    previousButtonAltText
  } = props

  let isNextButtonDisabled = currentSlide === totalItems - 3

  if (window.innerWidth < ScreenSizes.LG) {
    isNextButtonDisabled = currentSlide === totalItems - 1
  }

  return (
    <div className='carousel-button-group'>
      <ActionButton
        buttonType={ActionButtonTypes.PREVIOUS}
        isDisabled={currentSlide === 0}
        type='button'
        onClick={() => previous()}
        aria-label={previousButtonAltText}
        aria-disabled={currentSlide === 0}
        //authorable icons
        carouselActiveButtonAltText={nextButtonAltText}
        carouselDisabledButtonAltText={previousButtonAltText}
        nextButton={nextButton}
        previousButton={previousButton}
        tabIndex={currentSlide === 0 ? -1 : 0}
      />
      <ActionButton
        buttonType={ActionButtonTypes.NEXT}
        isDisabled={isNextButtonDisabled}
        type='button'
        onClick={() => next()}
        aria-label={nextButtonAltText}
        aria-disabled={isNextButtonDisabled}
        //authorable icons
        carouselActiveButtonAltText={nextButtonAltText}
        carouselDisabledButtonAltText={previousButtonAltText}
        nextButton={nextButton}
        previousButton={previousButton}
        tabIndex={isNextButtonDisabled ? -1 : 0}
      />
    </div>
  )
}

export default ReactStoreCarousel
